'use client';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
// Generally, this is used to determine styling for mobile devices
import { useEffect, useMemo, useState } from 'react';
// Setting to 768 as that's Tailwind's default "md" breakpoint
const MOBILE_BREAKPOINT = 768;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;
/**
 * Checks to see if the window size is within the boundary for displaying a
 * mobile view.
 *
 * @returns Whether or not the screen size is within the mobile breakpoint.
 */
export function isMobileScreenSize() {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}
// TODO: audit all usages and see if we can replace with useIsMobileUserAgent
// Select once per build so desktop consumers never subscribe to viewport changes.
export const useIsMobileStyle = import.meta.env
    .VITE_IS_PERPLEXITY_DESKTOP_BUILD
    ? useDesktopMobileStyle
    : useResponsiveMobileStyle;
function useDesktopMobileStyle() {
    return false;
}
function useResponsiveMobileStyle(options = {}) {
    const { device: { isWindowsApp }, } = useWebPlatform();
    const { evaluateOnInit = false } = options;
    const hasMobileUserAgent = useIsMobileUserAgent();
    const hasAndroidUserAgent = useIsAndroidUserAgent();
    const [isMobile, setIsMobile] = useState(() => {
        if (hasMobileUserAgent || hasAndroidUserAgent) {
            return true;
        }
        // Whether or not screen size should be evaluated on initial render. If a
        // component is rendered on the server, setting this to true will result in
        // a hydration mismatch.
        if (evaluateOnInit) {
            return isMobileScreenSize();
        }
        return false;
    });
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
        /**
         * Updates mobile state based on the media query matching.
         */
        function handleMobileMediaQuery() {
            setIsMobile(mobileMediaQuery.matches);
        }
        mobileMediaQuery.addEventListener('change', handleMobileMediaQuery);
        return () => {
            mobileMediaQuery.removeEventListener('change', handleMobileMediaQuery);
        };
    }, [hasMobileUserAgent]);
    useEffect(() => {
        if (!evaluateOnInit) {
            setIsMobile(isMobileScreenSize());
        }
    }, [evaluateOnInit]);
    return isMobile && !isWindowsApp;
}
// This is for functionality we only want on ACTUAL mobile devices of the right size
// These requests will have the "Mobile" string in their user agent
function useIsMobileUserAgent(initial = false) {
    const { device: { isMobile }, } = useWebPlatform();
    return isMobile ?? initial;
}
function useIsAndroidUserAgent(initial = false) {
    const { device: { isAndroid }, } = useWebPlatform();
    return isAndroid ?? initial;
}
export function useIsMobile(options = {}) {
    const isMobileStyle = useIsMobileStyle(options);
    const isMobileUserAgent = useIsMobileUserAgent();
    return useMemo(() => ({ isMobileStyle, isMobileUserAgent }), [isMobileStyle, isMobileUserAgent]);
}
//# sourceMappingURL=useMobileStyle.js.map